import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { Toaster as SonnerToaster, useSonner } from 'sonner';
import { useMediaQuery } from '@/components/shell/use-media-query';
import { shellOffsetPx } from '@/lib/shell';
import { useTheme } from '@/lib/theme';

export { toast } from 'sonner';

/** Sonner's own color variables, mapped to the graphite chrome so toasts read the same in both themes. */
const chromeVariables = {
  '--normal-bg': 'var(--color-chrome)',
  '--normal-bg-hover': 'var(--color-chrome-2)',
  '--normal-border': 'var(--color-chrome-border-strong)',
  '--normal-border-hover': 'var(--color-chrome-border-strong)',
  '--normal-text': 'var(--color-chrome-text)',
  '--gray2': 'var(--color-chrome-2)',
  '--gray5': 'var(--color-chrome-border-strong)',
  '--border-radius': 'var(--radius-md)',
} as CSSProperties;

/** Focus outline for the action and cancel buttons, drawn outside them on the toast's chrome fill. */
const chromeFocus =
  'focus-visible:outline-2! focus-visible:outline-offset-2! focus-visible:outline-chrome-accent! focus-visible:shadow-none!';

/**
 * Focus outline for the toast and its close button, drawn just inside their 1px border so it lies on the chrome fill
 * and not on the page around them. Sonner places the close button partly outside the toast's corner.
 */
const chromeInsetFocus =
  'focus-visible:outline-2! focus-visible:outline-offset-[-3px]! focus-visible:outline-chrome-accent! focus-visible:shadow-none!';

/** Sonner's mobile layout applies up to this width: full-width toasts with `mobileOffset`. */
const MOBILE_QUERY = '(max-width: 600px)';

/**
 * Least distance below the shell bar at which toasts start above 600px wide: under the title row of a page head with
 * breadcrumbs (at most 32px top padding, the 20px breadcrumb line and its 12px margin, a 28px title row) and a 4px
 * gap.
 */
const PAGE_HEAD_TITLE_BOTTOM = 96;

/** Gap between a toast and the page head above it. */
const HEAD_GAP = 4;

/** Least distance from the bottom of the viewport to toasts up to 600px wide. */
const MOBILE_BOTTOM = 12;

/**
 * Distance below the shell bar that clears the row of the page head holding the h1, its description, status and
 * actions, as laid out at the top of the document; at least PAGE_HEAD_TITLE_BOTTOM.
 */
function headClearance(): number {
  const row = document.querySelector('#main h1')?.closest('header > *');
  if (!row) return PAGE_HEAD_TITLE_BOTTOM;
  const bottom = row.getBoundingClientRect().bottom + window.scrollY - shellOffsetPx();
  return Math.max(PAGE_HEAD_TITLE_BOTTOM, Math.ceil(bottom + HEAD_GAP));
}

/**
 * Bottom area of the viewport that sticky elements cover, read from the document's bottom scroll padding (the
 * editor's Unsaved changes bar sets it to its height, its bottom offset and a gap).
 */
function reservedBottom(): number {
  const root = document.documentElement;
  return (
    Number.parseFloat(getComputedStyle(root).scrollPaddingBottom) ||
    Number.parseFloat(root.style.scrollPaddingBottom) ||
    0
  );
}

/** `measure()`, updated while `active` on window resizes and on resizes of `#main`. */
function useMeasured(active: boolean, measure: () => number, fallback: number): number {
  const [value, setValue] = useState(fallback);
  useEffect(() => {
    if (!active) return;
    const update = () => setValue(measure());
    update();
    window.addEventListener('resize', update);
    const main = document.getElementById('main');
    const resize = main ? new ResizeObserver(update) : undefined;
    if (main) resize?.observe(main);
    return () => {
      window.removeEventListener('resize', update);
      resize?.disconnect();
    };
  }, [active, measure]);
  return value;
}

/** reservedBottom(), updated while mounted when the root element's inline style changes. */
function useReservedBottom(): number {
  const [value, setValue] = useState(reservedBottom);
  useEffect(() => {
    const update = () => setValue(reservedBottom());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    return () => observer.disconnect();
  }, []);
  return value;
}

/**
 * Toasts sit at the top right above 600px wide, below the page head so they do not cover its title, status or
 * actions. Up to 600px they span the width at the bottom, above any area that sticky elements reserve there.
 */
export function Toaster() {
  const { resolved } = useTheme();
  const mobile = useMediaQuery(MOBILE_QUERY);
  const { toasts } = useSonner();
  const head = useMeasured(!mobile && toasts.length > 0, headClearance, PAGE_HEAD_TITLE_BOTTOM);
  const bottom = Math.max(MOBILE_BOTTOM, Math.ceil(useReservedBottom()));
  return (
    <SonnerToaster
      theme={resolved}
      position={mobile ? 'bottom-center' : 'top-right'}
      offset={{ top: `calc(var(--shell-offset) + ${head}px)`, right: 24 }}
      mobileOffset={{
        bottom: `calc(${bottom}px + env(safe-area-inset-bottom, 0px))`,
        right: 12,
        left: 12,
      }}
      closeButton
      duration={5000}
      style={chromeVariables}
      toastOptions={{
        classNames: {
          toast: `group gap-2.5! px-3.5! py-3! font-sans! text-sm! shadow-lg! ${chromeInsetFocus}`,
          title: 'font-medium!',
          description: 'text-chrome-muted!',
          actionButton: `h-7! rounded-sm! bg-chrome-accent! px-2.5! text-xs! font-medium! text-chrome! ${chromeFocus}`,
          cancelButton: `h-7! rounded-sm! bg-chrome-3! px-2.5! text-xs! text-chrome-text! ${chromeFocus}`,
          closeButton: `size-6! rounded-sm! border-chrome-border-strong! bg-chrome-2! text-chrome-text! hover:bg-chrome-3! ${chromeInsetFocus}`,
          success: '[&_[data-icon]]:text-chrome-success!',
          error: '[&_[data-icon]]:text-chrome-danger!',
          warning: '[&_[data-icon]]:text-chrome-warning!',
          info: '[&_[data-icon]]:text-chrome-accent!',
        },
      }}
    />
  );
}
